import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { AdminPageHeader } from '../../Admin';
import {
    LEGEND_DETAILS,
    NAVIGATION_DATA_TYPES,
    QUERY_KEYS,
    ROUTES,
    SCORECARD_REPORT,
    SYMBOL,
} from '../../../constants';
import { getAllDocumentRequestByAccount, getAllScoreCardDetails, popup } from '../../../utils';
import { CircleSpinner } from '../../../assets/svg';
import { setNavigationState } from '../../../redux';
import { NoDataFound } from '../../shared/NoDataFound';

export const ScorecardLandingPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const accountId = useSelector((state) => state.accountArray.selectedAccount);
    const selectedAccountName = useSelector((state) => state.accountArray.selectedAccountName);

    const scQuery = useQuery({
        queryKey: [QUERY_KEYS.SCORECARD_LIST, accountId],
        queryFn: async () =>
            await getAllScoreCardDetails({
                account_id: accountId,
                list: true,
                orderByGrade: true,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
    });

    if (scQuery.isError) popup('error', scQuery.error.message);

    const docReqQuery = useQuery({
        queryKey: [QUERY_KEYS.DOCUMENT_REQUEST_BY_ID, accountId],
        queryFn: async () =>
            await getAllDocumentRequestByAccount({
                account_id: accountId,
                filter: { 'not-platforms': null },
            }),
        select: (res) => res.data,
    });

    if (docReqQuery.isError) popup('error', docReqQuery.error.message);

    const onClickHandler = (sc) => {
        dispatch(setNavigationState({ id: sc.platform_id, type: NAVIGATION_DATA_TYPES.PLATFORM }));
        navigate(ROUTES.CUSTOMER.SCORE_CARD_REPORT);
    };

    return (
        <main className='scorecard-landing-page'>
            {/* PAGE HEADER  */}
            <AdminPageHeader
                heading={SCORECARD_REPORT.SCORE_CARD_LISTING_PAGE.HEADER_HEADING}
                backbutton
                showSearchBar={false}
                headerButtonChildren={
                    <button
                        type='button'
                        className='ssc-primary-green-btn-small me-4'
                        title='Go to Scorecard Report Page'
                        onClick={() => navigate(ROUTES.CUSTOMER.SCORE_CARD_REPORT)}
                    >
                        GO
                    </button>
                }
                showAddButton={scQuery.data.count > 0}
            />
            <section className='sc-grade-container position-relative'>
                {scQuery.isFetching ? (
                    <CircleSpinner absolute size='3rem' />
                ) : scQuery.data.count > 0 ? (
                    <div className='grade-card-grid-flow'>
                        {scQuery.data.data.map((sc) => {
                            const ptGrade = sc?.grade || 'grey';
                            const title =
                                LEGEND_DETAILS.find((lg) => lg.color === ptGrade)?.description ||
                                '';
                            return (
                                <div
                                    key={sc.id}
                                    className='grade-card'
                                    onClick={() => onClickHandler(sc)}
                                    aria-details={sc.platform_name}
                                    aria-labelledby={ptGrade}
                                    aria-description={title}
                                >
                                    <div
                                        className={`grade-circle ${sc.grade}-circle`}
                                        title={title}
                                    ></div>
                                    <span className='pt-name text-truncate' title={title}>
                                        <span> {sc.platform_name}</span>
                                        <sup>{SYMBOL.REGISTERED}</sup>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <NoDataFound
                        heading='No Scorecard Available'
                        message={
                            <>
                                Your support team hasn&apos;t created any scorecards for{' '}
                                <b>{selectedAccountName}</b> yet. Please reach out to your Client
                                Success Manager to request one.
                            </>
                        }
                        bg="var(--col-white)"
                    />
                )}
            </section>
            <div className='divider'></div>
            <footer className='sc-grade-footer'>
                {docReqQuery.isFetching ? (
                    <CircleSpinner absolute size='3rem' />
                ) : docReqQuery.data.length > 0 ? (
                    <div className='sc-grade-footer-body'>
                        <h5>Complete Regulatory Documents:</h5>
                        <div className='reg-doc-list-container'>
                            <ul className='reg-doc-ul'>
                                {docReqQuery.data.map((doc) => (
                                    <li key={doc.id} className='reg-doc-li'>
                                        <Link
                                            to={ROUTES.CUSTOMER.DOCUMENTS}
                                            state={{ requestID: doc.id }}
                                        >
                                            <span
                                                className='link-text'
                                                title={`Platforms: [${JSON.parse(doc.platforms)
                                                    .map((p) => p.platform_name)
                                                    .join(', ')}]`}
                                            >
                                                {doc.document_category_name}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : null}
            </footer>
        </main>
    );
};
